import React from 'react';
import { Box, Text, Card, Flex, Heading, Grid, Img, TextInput, Switch, Badge } from 'pounceV2';
import AutoStreamTypeSVG from 'Assets/illustrations/auto-stream-type.svg';
import { LogStreamType } from 'Generated/schema';
import EditorV3 from 'Components/Editor/EditorV3';
import { useTheme } from '@mui/material';
import { STREAM_TYPES } from 'Source/constants';
import LogStreamTypePickerV3 from './LogStreamTypePickerV3';
import {
  eventLogs,
  xmlEventLogs,
  streamTypesExample,
  cloudwatchEventLogsWithEnvelope,
} from './constants';

export type SetLogStreamTypePropsV3 = {
  streamType: LogStreamType;
  jsonArrayEnvelopeField?: string;
  xmlRootElement?: string;
  excludeCloudwatch: boolean;
  /** When provided, only these stream types are available. If a single type, the picker is hidden. */
  streamTypes?: LogStreamType[];
  retainEnvelopeFields?: boolean;
  onStreamTypeChange: (logStreamType: LogStreamType) => void;
  onRetainEnvelopeFieldsChange?: (value: boolean) => void;
  // TODO:NICKK remove this field, this should always be provided.
  // This is a quick hack to avoid setting up the callback in infer schema pages until it is supported
  withEnvelopeField?: boolean;
  withXmlRootElement?: boolean;
  onEnvelopeFieldChange?: (envelopeField: string) => void;
  onXmlRootElementChange?: (xmlRootElement: string) => void;
};

const SetLogStreamTypeV3 = ({
  streamType,
  jsonArrayEnvelopeField,
  xmlRootElement,
  excludeCloudwatch,
  streamTypes,
  retainEnvelopeFields,
  withEnvelopeField,
  withXmlRootElement,
  onStreamTypeChange,
  onRetainEnvelopeFieldsChange,
  onEnvelopeFieldChange,
  onXmlRootElementChange,
}: SetLogStreamTypePropsV3) => {
  const theme = useTheme();
  let logStreamTypeExample = streamTypesExample[streamType];
  const [hasEnvelopeField, setHasEnvelopeField] = React.useState(!!jsonArrayEnvelopeField);
  const [hasXmlRootElement, setHasXmlRootElement] = React.useState(!!xmlRootElement);

  const hasNestedEnvelopeWarning =
    streamType === LogStreamType.JsonArray &&
    (jsonArrayEnvelopeField?.includes('.') ||
      jsonArrayEnvelopeField?.includes('[') ||
      jsonArrayEnvelopeField?.includes('$'));

  if (streamType === LogStreamType.JsonArray && !!jsonArrayEnvelopeField) {
    logStreamTypeExample = `{ "${jsonArrayEnvelopeField}": ${logStreamTypeExample} }`;
  }

  if (streamType === LogStreamType.Xml && !!xmlRootElement) {
    // Add indentation to each line of the XML example when wrapped in a root element
    const xmlLines = logStreamTypeExample.split('\n');
    const indentedXmlLines = xmlLines.map(line => `    ${line}`).join('\n');
    logStreamTypeExample = `<${xmlRootElement}>\n${indentedXmlLines}\n</${xmlRootElement}>`;
  }

  // Use appropriate event logs based on stream type and envelope toggle
  const getDisplayEventLogs = () => {
    if (streamType === LogStreamType.Xml) {
      return xmlEventLogs;
    }
    if (streamType === LogStreamType.CloudWatchLogs && retainEnvelopeFields) {
      return cloudwatchEventLogsWithEnvelope;
    }
    return eventLogs;
  };
  const displayEventLogs = getDisplayEventLogs();

  const isCloudWatch = streamType === LogStreamType.CloudWatchLogs;

  const cloudwatchSwitch = isCloudWatch && (
    <Card p={4}>
      <Flex align="center" gap={2}>
        <Text>{'Retain envelope fields in `p_header` field'}</Text>
        <Badge borderStyle="rectangle" emphasized variant="neutral-outline" size="small">
          New
        </Badge>
        <Box ml={2}>
          <Switch
            aria-label="Retain envelope fields"
            checked={!!retainEnvelopeFields}
            onChange={e => {
              onRetainEnvelopeFieldsChange?.(e.target.checked);
            }}
          />
        </Box>
      </Flex>
    </Card>
  );

  const streamTypeContent = (
    <Flex direction="column" gap={4} m={4}>
      {streamType === LogStreamType.JsonArray && withEnvelopeField && (
        <Card p={4}>
          <Flex align="center" gap={2}>
            <Text>Is the JSON Array enclosed in a field?</Text>
            <Badge borderStyle="rectangle" emphasized variant="neutral-outline" size="small">
              NEW
            </Badge>
            <Box ml={2}>
              <Switch
                aria-label="Has Envelope Field"
                checked={hasEnvelopeField}
                onChange={e => {
                  setHasEnvelopeField(e.target.checked);
                  if (!e.target.checked) {
                    onEnvelopeFieldChange(undefined);
                  }
                }}
              />
            </Box>
          </Flex>
          {hasEnvelopeField && (
            <Box>
              <Text variant="body/sm" color={theme.palette.tokens.content.text.neutral[2]} my={2}>
                If the JSON array is enclosed within a specific field, you’ll need to provide the
                name of that field in the input below <br /> to ensure proper processing.
              </Text>
              <TextInput
                value={jsonArrayEnvelopeField}
                onChange={e => onEnvelopeFieldChange(e.target.value)}
                placeholder="Records"
                label="Enclosing Field Name"
              />
              {hasNestedEnvelopeWarning && (
                <Text mt={1} color={theme.palette.tokens.content.text.semantic.suggest.base}>
                  Nested fields are not supported. Please ensure the field is at the root
                </Text>
              )}
            </Box>
          )}
        </Card>
      )}

      {streamType === LogStreamType.Xml && withXmlRootElement && (
        <Card p={4}>
          <Flex align="center" gap={2}>
            <Text>Are the XML events enclosed in a root element?</Text>
            <Badge borderStyle="rectangle" emphasized variant="neutral-outline" size="small">
              NEW
            </Badge>
            <Box ml={2}>
              <Switch
                aria-label="Has XML Root Element"
                checked={hasXmlRootElement}
                onChange={e => {
                  setHasXmlRootElement(e.target.checked);
                  if (!e.target.checked) {
                    onXmlRootElementChange(undefined);
                  }
                }}
              />
            </Box>
          </Flex>
          {hasXmlRootElement && (
            <Box>
              <Text variant="body/sm" color={theme.palette.tokens.content.text.neutral[2]} my={2}>
                If the XML events are enclosed in a root element, you can specify the name of that
                element below to extract individual events.
              </Text>
              <TextInput
                value={xmlRootElement}
                onChange={e => onXmlRootElementChange?.(e.target.value)}
                placeholder="e.g., logs, events, data"
                label="XML Root Element"
              />
            </Box>
          )}
        </Card>
      )}

      {cloudwatchSwitch}

      <Text fontWeight="medium">Input File</Text>
      <EditorV3
        maxHeight={380}
        readOnly
        minHeight={17}
        hideLineNumbers
        language="json"
        value={logStreamTypeExample || ''}
      />
      <Text fontWeight="medium">Panther Reads</Text>
      <Flex gap={2} direction="column">
        {displayEventLogs.map((event, index) => (
          <EditorV3
            key={`${streamType}-${index}`}
            readOnly
            hideLineNumbers
            hideScrollbar
            minHeight={17}
            language="json"
            value={event}
          />
        ))}
      </Flex>
    </Flex>
  );

  const isSingleStreamType = streamTypes?.length === 1;

  if (isSingleStreamType) {
    return (
      <Box p={4}>
        <Heading variant="h6" mb={4}>
          Select the format of the logs that will be ingested from this source.
        </Heading>
        <Card variant="inverseRaised">
          <Flex direction="column" gap={4} m={4} mb={0}>
            <Heading variant="h5" fontWeight="bold">
              {STREAM_TYPES[streamTypes[0]].title}
            </Heading>
          </Flex>
          {streamTypeContent}
        </Card>
      </Box>
    );
  }

  return (
    <Box p={4}>
      <Heading variant="h6" mb={4}>
        Select the format of the logs that will be ingested from this source. Click each one to see
        an example of what the input file should look like and what Panther will read.
      </Heading>
      <Grid templateColumns="3fr 7fr" gap={4}>
        <LogStreamTypePickerV3
          value={streamType}
          onChange={onStreamTypeChange}
          excludeCloudwatch={excludeCloudwatch}
        />
        <Card variant="inverseRaised">
          {streamType === LogStreamType.Auto ? (
            <Box p={3}>
              <Heading variant="h5" fontWeight="bold">
                Automatic Identification
              </Heading>
              <Text color={theme.palette.tokens.content.text.neutral[2]} mt={2}>
                Auto automatically identifies the source&apos;s log format.
              </Text>
              <Text color={theme.palette.tokens.content.text.neutral[2]} mt={4} mb={6}>
                Auto also detects the appropriate log format per file, meaning it can process
                multiple log formats within a single source without requiring manual intervention.
              </Text>
              <Flex justify="center">
                <Img src={AutoStreamTypeSVG} nativeWidth={190} alt="Auto" />
              </Flex>
            </Box>
          ) : (
            <>
              {isCloudWatch && (
                <Flex direction="column" gap={4} m={4} mb={0}>
                  <Heading variant="h5" fontWeight="bold">
                    {STREAM_TYPES[LogStreamType.CloudWatchLogs].title}
                  </Heading>
                </Flex>
              )}
              {streamTypeContent}
            </>
          )}
        </Card>
      </Grid>
    </Box>
  );
};

export default SetLogStreamTypeV3;
